import { useGetSubscription } from "@/api/stripe";
import { useUserInfo } from "@/api/user";
import { formatCurrencyFromCents, formatInteger } from "@/utils/currency";
import React from "react";

interface UsageProgressBarProps {
  className?: string;
}

export const UsageProgressBar: React.FC<UsageProgressBarProps> = ({
  className = "",
}) => {
  const { data: userInfo, isLoading: isUserLoading } = useUserInfo();
  const { data: subscription, isLoading: isSubscriptionLoading } =
    useGetSubscription();

  const userSubscription = userInfo?.subscription;
  const usage = userInfo?.usage;
  const currentPlan = subscription?.data.stripe_subscription;

  // Don't render if no subscription/usage data or still loading
  if (isUserLoading || isSubscriptionLoading || !userSubscription || !usage) {
    return null;
  }

  // Extract individual user data
  const myCreditsUsed = usage.current_period_user_credit_count || 0;
  const includedCreditsPerSeat =
    userSubscription.included_credits_per_seat || 0;
  const subscriptionStatus = userSubscription.status;
  const isTrialing = subscriptionStatus === "trialing";

  // Individual user progress (only included credits, no on-demand)
  const myUsedCredits = Math.min(myCreditsUsed, includedCreditsPerSeat);
  const myIncludedPercentage =
    includedCreditsPerSeat > 0
      ? (myUsedCredits / includedCreditsPerSeat) * 100
      : 0;

  // Organization on-demand data (only for active subscriptions)
  const orgOnDemandSpent = usage.current_period_org_credit_overage_count || 0;
  const maxOverageSpend =
    userSubscription.max_credit_overage_spend_per_period || 0;
  const creditPrice = currentPlan?.credit_price || 0;

  // Convert overage count to dollar amount
  const orgOnDemandSpentDollars = (orgOnDemandSpent * creditPrice) / 100;
  const maxOverageSpendDollars = maxOverageSpend / 100;

  // Organization on-demand percentage (show if there's any budget limit set, including $0)
  const hasOnDemandBudget = maxOverageSpend >= 0 && maxOverageSpend !== -1;
  const orgOnDemandPercentage = hasOnDemandBudget
    ? maxOverageSpendDollars > 0
      ? Math.min((orgOnDemandSpentDollars / maxOverageSpendDollars) * 100, 100)
      : orgOnDemandSpentDollars > 0
        ? 100
        : 0 // For $0 budget, show 100% if any spending occurred
    : 0;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Individual User Credit Usage Bar */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">My Credit Usage</h3>
          <div className="text-sm text-muted-foreground">
            {formatInteger(myCreditsUsed)} used /{" "}
            {formatInteger(includedCreditsPerSeat)} included
          </div>
        </div>

        <div className="space-y-2">
          {/* Individual user progress bar */}
          <div className="relative h-3 w-full overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-700">
            <div
              className="absolute left-0 top-0 h-full bg-lc-active transition-all duration-300"
              style={{ width: `${myIncludedPercentage}%` }}
            />
          </div>

          {/* Individual usage legend */}
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-lc-active rounded-full" />
              <span className="text-muted-foreground">
                Used: {formatInteger(myUsedCredits)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-neutral-200 dark:bg-neutral-700 rounded-full border border-neutral-300 dark:border-neutral-600" />
              <span className="text-muted-foreground">
                Unused:{" "}
                {formatInteger(
                  Math.max(0, includedCreditsPerSeat - myCreditsUsed),
                )}
              </span>
            </div>
            {myCreditsUsed > includedCreditsPerSeat && (
              <div className="ml-auto text-neutral-600 dark:text-neutral-400 font-medium">
                {isTrialing ? "Trial limit exceeded" : "Over included limit"}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Organization On-Demand Usage Bar - only show for active subscriptions */}
      {!isTrialing && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">
              Organization On-Demand Usage
            </h3>
            <div className="text-sm text-muted-foreground">
              {hasOnDemandBudget
                ? `${formatCurrencyFromCents(Math.round(orgOnDemandSpentDollars * 100))} / ${formatCurrencyFromCents(maxOverageSpend)}`
                : `${formatCurrencyFromCents(Math.round(orgOnDemandSpentDollars * 100))} spent`}
            </div>
          </div>

          <div className="space-y-2">
            {/* Organization on-demand progress bar */}
            <div className="relative h-3 w-full overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-700">
              {hasOnDemandBudget && (
                <div
                  className="absolute left-0 top-0 h-full bg-neutral-500 transition-all duration-300"
                  style={{ width: `${orgOnDemandPercentage}%` }}
                />
              )}
            </div>

            {/* Organization usage legend */}
            <div className="flex items-center gap-4 text-xs">
              {hasOnDemandBudget ? (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-neutral-500 rounded-full" />
                    <span className="text-muted-foreground">
                      Spent:{" "}
                      {formatCurrencyFromCents(
                        Math.round(orgOnDemandSpentDollars * 100),
                      )}
                    </span>
                  </div>
                  {maxOverageSpendDollars > 0 && (
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-neutral-200 dark:bg-neutral-700 rounded-full border border-neutral-300 dark:border-neutral-600" />
                      <span className="text-muted-foreground">
                        Remaining:{" "}
                        {formatCurrencyFromCents(
                          Math.round(
                            (maxOverageSpendDollars - orgOnDemandSpentDollars) *
                              100,
                          ),
                        )}
                      </span>
                    </div>
                  )}
                  {(maxOverageSpendDollars === 0 &&
                    orgOnDemandSpentDollars > 0) ||
                  (maxOverageSpendDollars > 0 &&
                    orgOnDemandSpentDollars >= maxOverageSpendDollars) ? (
                    <div className="ml-auto text-neutral-600 dark:text-neutral-400 font-medium">
                      Budget exceeded
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-neutral-500 rounded-full" />
                  <span className="text-muted-foreground">
                    Total spent:{" "}
                    {formatCurrencyFromCents(
                      Math.round(orgOnDemandSpentDollars * 100),
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
